import { useEffect, useState } from "react";
import { Platform } from "react-native";

import { trpc } from "../trpc";
import { iapBridge } from "./iap-bridge";
import { useCheckoutRail } from "./use-checkout-rail";
import type { IapErrorKind } from "./use-checkout-rail";

// Re-export so callers only need to import from use-paywall
export type { IapErrorKind };

export interface PaywallVerticalInfo {
  slug: string;
  name: string;
  shortName: string;
  jurisdictionName: string | null;
  pricing: {
    oneOffCents: number;
    subscriptionMonthlyCents: number | null;
    subscriptionAnnualCents: number | null;
  };
  iapProductIds: {
    oneoff?: string;
    monthly?: string;
    annual?: string;
  };
  // Item 8: Localized store pricing from the native IAP store.
  // Populated on iOS/Android; null on web (Stripe shows USD).
  storePricing?: {
    oneoff?: string;
    monthly?: string;
    annual?: string;
  };
}

interface UsePaywallReturn {
  info: PaywallVerticalInfo | null;
  busy: boolean;
  error: string | null;
  /** Item 6: distinct kind for conflict / already-owned errors */
  errorKind: IapErrorKind | null;
  checkout: (tier: "oneoff" | "monthly" | "annual") => Promise<void>;
}

// Encapsulates the paywall's vertical-info fetch + checkout mutation.
// Resolves jurisdictional friendly names so the orchestrator can
// render "Full California CDL access until you pass." without an
// extra lookup.
export function usePaywall(
  verticalSlug: string | null,
  jurisdictionCode: string | null,
): UsePaywallReturn {
  const rail = useCheckoutRail();
  const [info, setInfo] = useState<PaywallVerticalInfo | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!verticalSlug) {
      setLoadError("No credential selected.");
      return;
    }
    void (async () => {
      try {
        const v = await trpc.vertical.bySlug.query({ slug: verticalSlug });
        if (!v) {
          setLoadError("Vertical not found.");
          return;
        }
        const j = jurisdictionCode
          ? v.jurisdictions.find((x) => x.code === jurisdictionCode)
          : null;

        const iapProductIds = v.pricing.iapProductIds ?? {};

        // Item 8: Fetch live store pricing on native platforms.
        let storePricing: PaywallVerticalInfo["storePricing"] | undefined;
        if (Platform.OS !== "web") {
          try {
            const skus = [
              iapProductIds.oneoff,
              iapProductIds.monthly,
              iapProductIds.annual,
            ].filter(Boolean) as string[];

            if (skus.length > 0) {
              const storeProducts = await iapBridge.getStoreProducts(skus);
              storePricing = {};
              for (const sp of storeProducts) {
                if (sp.productId === iapProductIds.oneoff) {
                  storePricing.oneoff = sp.localizedPrice;
                }
                if (sp.productId === iapProductIds.monthly) {
                  storePricing.monthly = sp.localizedPrice;
                }
                if (sp.productId === iapProductIds.annual) {
                  storePricing.annual = sp.localizedPrice;
                }
              }
            }
          } catch {
            // Store price fetch failure is non-fatal; fall back to USD prices.
            storePricing = undefined;
          }
        }

        setInfo({
          slug: v.slug,
          name: v.name,
          shortName: v.shortName,
          jurisdictionName: j?.name ?? null,
          pricing: {
            oneOffCents: v.pricing.oneOffCents,
            subscriptionMonthlyCents: v.pricing.subscriptionMonthlyCents,
            subscriptionAnnualCents: v.pricing.subscriptionAnnualCents,
          },
          iapProductIds,
          storePricing,
        });
      } catch (err) {
        setLoadError(err instanceof Error ? err.message : "failed to load");
      }
    })();
  }, [verticalSlug, jurisdictionCode]);

  async function checkout(
    tier: "oneoff" | "monthly" | "annual",
  ): Promise<void> {
    if (!verticalSlug) return;
    await rail.checkout({
      verticalSlug,
      jurisdictionCode,
      tier,
      iapProductId: info?.iapProductIds[tier] ?? null,
    });
  }

  return {
    info,
    busy: rail.busy,
    error: loadError ?? rail.error,
    errorKind: rail.errorKind,
    checkout,
  };
}

export function fmtUsd(cents: number): string {
  const dollars = cents / 100;
  return dollars % 1 === 0
    ? `$${dollars.toFixed(0)}`
    : `$${dollars.toFixed(2)}`;
}
